import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import '@fontsource/nunito/500.css';
import '@fontsource/nunito/800.css';
import '@fontsource/nunito/900.css';
import { fitLabelSize, LABEL_RADIUS, labelRotation, nextWheelBatch, point, poolForMode, rotationForSelection, segmentPath } from './wheelGeometry';
import { createWeightedWheelSelection } from './wheelSelection';
import { playMiniTaskSound, prepareMiniTaskSound, readMiniTaskSoundEnabled } from '../soundEffects';
import './wheel.css';

const colors = ['#fff08a', '#ffc1df', '#aedcff', '#c3f49d', '#cdb7fb', '#ffd1a2', '#a9eeea', '#ecc3ef'];
const emptyEntries = [];

function ReadingText({ children }) {
  const slot = useRef(null);
  const text = useRef(null);
  const [size, setSize] = useState(48);
  useLayoutEffect(() => {
    let active = true;
    const fit = () => {
      if (!active || !slot.current || !text.current) return;
      const width = slot.current.clientWidth - 4;
      const height = slot.current.clientHeight;
      const bounds = text.current.getBoundingClientRect();
      if (width <= 0 || height <= 0 || bounds.width <= 0) return;
      const current = parseFloat(getComputedStyle(text.current).fontSize);
      const next = Math.max(18, Math.floor(Math.min(height / 1.12, current * width / bounds.width)));
      setSize((previous) => previous === next ? previous : next);
    };
    fit();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    observer?.observe(slot.current); observer?.observe(text.current);
    document.fonts?.ready.then(fit);
    window.addEventListener('resize', fit);
    return () => { active = false; observer?.disconnect(); window.removeEventListener('resize', fit); };
  }, [children]);
  return <Box ref={slot} className="word-wheel-reading-line"><Typography component="span" ref={text} className="word-wheel-reading" style={{ fontSize: size }}>{children}</Typography></Box>;
}

function PandaFace() {
  return <svg viewBox="0 0 80 80" aria-hidden="true" focusable="false" className="word-wheel-panda">
    <circle cx="19" cy="19" r="12" fill="#29213e" /><circle cx="61" cy="19" r="12" fill="#29213e" />
    <ellipse cx="40" cy="43" rx="31" ry="29" fill="#fffdf9" />
    <ellipse cx="27" cy="40" rx="10" ry="12" transform="rotate(22 27 40)" fill="#29213e" />
    <ellipse cx="53" cy="40" rx="10" ry="12" transform="rotate(-22 53 40)" fill="#29213e" />
    <circle cx="29" cy="38" r="3.4" fill="white" /><circle cx="51" cy="38" r="3.4" fill="white" />
    <circle cx="22" cy="54" r="5" fill="#ffdce9" /><circle cx="58" cy="54" r="5" fill="#ffdce9" />
    <path d="M35 49 Q40 45 45 49 Q44 55 40 55 Q36 55 35 49" fill="#29213e" />
    <path d="M40 55 V59 M32 58 Q40 66 48 58" fill="none" stroke="#29213e" strokeWidth="2.5" strokeLinecap="round" />
  </svg>;
}

function EmptyWheel() {
  return <Box className="word-wheel-empty">
    <Box className="word-wheel-empty-art" aria-hidden="true"><Box className="word-wheel-empty-halo" /><PandaFace /><span>✦</span><span>✧</span></Box>
    <Typography variant="h3" component="h3">Çarkımız hazırlanıyor</Typography>
    <Typography className="word-wheel-empty-description">Henüz hazır içerik yok. Öğrenilen harfler ve onaylı içerikler tamamlandığında birlikte çevireceğiz.</Typography>
  </Box>;
}

export function WheelPlayer({ entries = emptyEntries, pendingEntries, paused = false, mode: controlledMode, onModeChange, hideModes = false, soundEnabled }) {
  const [currentEntries, setCurrentEntries] = useState(entries);
  const spinAfterUpdate = useRef(false);
  const spinLatest = useRef(null);
  const applied = useRef(null);
  const rotor = useRef(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  useEffect(() => { setCurrentEntries(entries); }, [entries]);
  const [localMode, setLocalMode] = useState('mixed');
  const mode = controlledMode ?? localMode;
  const setMode = onModeChange || setLocalMode;
  const [showSyllables, setShowSyllables] = useState(false);
  const pool = useMemo(() => poolForMode(currentEntries, mode), [currentEntries, mode]);
  const initialBatch = useMemo(() => nextWheelBatch(pool), [pool]);
  const [refreshedBatch, setRefreshedBatch] = useState(null);
  const batch = refreshedBatch?.pool === pool ? refreshedBatch : initialBatch;
  const { items } = batch;
  const canRefresh = pool.length > items.length;
  const refreshHintId = useId();
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const pending = useRef(null);
  const locked = useRef(false);
  const timer = useRef(null);
  const selector = useRef(null);
  if (!selector.current) selector.current = createWeightedWheelSelection();
  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;
  const isSoundEnabled = () => soundEnabledRef.current ?? readMiniTaskSoundEnabled();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const labelElements = useRef([]);
  const [labelSizes, setLabelSizes] = useState([]);

  useLayoutEffect(() => {
    let active = true;
    const fit = () => {
      if (!active) return;
      const sizes = items.map((_, index) => {
        const element = labelElements.current[index];
        if (!element || typeof element.getBBox !== 'function') return 24;
        const bounds = element.getBBox();
        const size = Number(element.getAttribute('font-size'));
        const x = Number(element.getAttribute('x')), y = Number(element.getAttribute('y'));
        return fitLabelSize({ x: (bounds.x - x) / size, y: (bounds.y - y) / size,
          width: bounds.width / size, height: bounds.height / size }, index, items.length);
      });
      setLabelSizes((previous) => previous.length === sizes.length && previous.every((value, index) => value === sizes[index]) ? previous : sizes);
    };
    fit(); document.fonts?.ready.then(fit);
    document.fonts?.addEventListener('loadingdone', fit);
    return () => { active = false; document.fonts?.removeEventListener('loadingdone', fit); };
  }, [items]);

  useEffect(() => {
    setResult(null); setSelectedIndex(null); setRotation(0); setSpinning(false);
    locked.current = false; pending.current = null;
    clearTimeout(timer.current);
    if (spinAfterUpdate.current) {
      spinAfterUpdate.current = false;
      timer.current = setTimeout(() => spinLatest.current?.(), 0);
    }
    return () => clearTimeout(timer.current);
  }, [items]);

  const finish = () => {
    if (pausedRef.current || !pending.current) return;
    const selection = pending.current;
    pending.current = null; locked.current = false;
    clearTimeout(timer.current);
    selector.current.complete(selection.item);
    setResult(selection.item); setSelectedIndex(selection.index); setSpinning(false);
    if (isSoundEnabled()) playMiniTaskSound('correct', true);
  };

  const spin = () => {
    if (paused || locked.current) return;
    if (pendingEntries && applied.current !== pendingEntries) {
      applied.current = pendingEntries;
      spinAfterUpdate.current = true;
      setCurrentEntries(pendingEntries);
      return;
    }
    if (!items.length) return;
    prepareMiniTaskSound(isSoundEnabled());
    locked.current = true;
    const selection = selector.current.select(items);
    const { index } = selection;
    pending.current = selection;
    setSelectedIndex(null); setResult(null);
    setRotation(rotationForSelection(rotation, index, items.length));
    if (reducedMotion || items.length === 1) { finish(); return; }
    setSpinning(true);
    // Transition completion is primary; fallback handles hidden tabs/browser interruption.
    timer.current = setTimeout(finish, 3300);
  };
  spinLatest.current = spin;
  useLayoutEffect(() => {
    if (!paused || !spinning || !rotor.current) return undefined;
    const element = rotor.current;
    const frozen = getComputedStyle(element).transform;
    const target = element.style.transform;
    const transition = element.style.transition;
    clearTimeout(timer.current);
    element.style.transition = 'none'; element.style.transform = frozen;
    return () => {
      // Continue the same selected question and rotation after settings.
      void element.getBoundingClientRect();
      element.style.transition = transition; element.style.transform = target;
      timer.current = setTimeout(finish, 3300);
    };
    // The selected spin is immutable until it finishes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, spinning]);

  const refresh = () => {
    if (paused || locked.current) return;
    if (pendingEntries && applied.current !== pendingEntries) {
      applied.current = pendingEntries; setCurrentEntries(pendingEntries); return;
    }
    if (!canRefresh) return;
    setResult(null); setSelectedIndex(null); setRotation(0);
    setRefreshedBatch(previous => nextWheelBatch(pool, previous?.pool === pool ? previous : initialBatch));
  };

  return <Box className={`word-wheel-player${items.length ? ' word-wheel-player--ready' : ''}`}>
    <Box className="word-wheel-toolbar">
      {!hideModes && <ToggleButtonGroup exclusive value={mode} onChange={(_, next) => {
        if (!next) return;
        if (pendingEntries && applied.current !== pendingEntries) { applied.current = pendingEntries; setCurrentEntries(pendingEntries); }
        setMode(next);
      }} disabled={spinning || paused} size="small" aria-label="İçerik türü" className="word-wheel-mode-switch">
        <ToggleButton value="syllable">Heceler</ToggleButton><ToggleButton value="word">Kelimeler</ToggleButton><ToggleButton value="mixed">Karışık</ToggleButton>
      </ToggleButtonGroup>}
      {pool.length > 0 && <Box className="word-wheel-refresh-control">
        <Button variant="outlined" startIcon={<RefreshRoundedIcon />} className="word-wheel-refresh-button" onClick={refresh} disabled={spinning || paused || (!canRefresh && (!pendingEntries || applied.current === pendingEntries))} aria-describedby={!canRefresh ? refreshHintId : undefined}>İçeriği Yenile</Button>
        {!canRefresh && <Typography id={refreshHintId} className="word-wheel-refresh-note">Tüm uygun içerikler çarkta.</Typography>}
      </Box>}
      <Box role="status" className="word-wheel-refresh-status">{batch !== initialBatch ? `Çark yenilendi. Seçenekler: ${items.map(item => item.text).join(', ')}` : ''}</Box>
    </Box>
    {!items.length ? <EmptyWheel /> :
      <Box className="word-wheel-layout">
        <Box className="word-wheel-reading-column">
        <Paper elevation={0} className="word-wheel-result">
          <Box className="word-wheel-result-heading"><StarRoundedIcon aria-hidden="true" /><Typography>{result ? `Seçilen ${result.kind === 'word' ? 'kelime' : 'hece'}` : 'Birlikte okuyalım'}</Typography><StarRoundedIcon aria-hidden="true" /></Box>
          <Box aria-live="polite" aria-atomic="true" className="word-wheel-reading-surface">
            <ReadingText>{result ? result.text.toLocaleLowerCase('tr-TR') : 'Çarkı çevir!'}</ReadingText>
            <Box className="word-wheel-reading-help">{result && showSyllables && result.kind === 'word' ? <Typography data-testid="wheel-syllables" className="word-wheel-syllables">{result.syllables.join(' – ')}</Typography> : <Typography className="word-wheel-reading-note">{spinning ? 'Bakalım hangisi gelecek?' : result ? 'Hep birlikte okuyalım!' : 'Hazırsan başlayalım!'}</Typography>}</Box>
          </Box>
          <FormControlLabel className="word-wheel-syllable-toggle" labelPlacement="start" control={<Switch checked={showSyllables} onChange={(event) => setShowSyllables(event.target.checked)} />} label={<Box><Typography component="span">Hecelemeyi Göster</Typography><Typography component="span" className="word-wheel-toggle-note" aria-hidden="true">İstediğin zaman aç / kapat</Typography></Box>} />
        </Paper>
        <Box className="word-wheel-hint"><LightbulbRoundedIcon aria-hidden="true" /><Typography>Önce oku, ihtiyaç olursa hecelemeyi aç.</Typography></Box>
        </Box>
        <Box className="word-wheel-frame">
          <span className="word-wheel-pointer" aria-hidden="true" />
          <svg className="word-wheel-svg" viewBox="0 0 400 400" role="img" aria-label={`Çark seçenekleri: ${items.map((item) => item.text).join(', ')}`} data-label-mode="tangential">
            <circle cx="200" cy="200" r="193" fill="white" stroke="#e9d9ff" strokeWidth="1.5" />
            <g ref={rotor} className="word-wheel-rotor"
              style={{ transform: `rotate(${rotation}deg)`, transition: spinning && !reducedMotion ? 'transform 3s cubic-bezier(.12,.7,.12,1)' : 'none' }}
              onTransitionEnd={(event) => { if (event.target === event.currentTarget && event.propertyName === 'transform') finish(); }}>
            {items.map((item, index) => {
              const angle = -90 + index * 360 / items.length;
              const [x, y] = point(angle, LABEL_RADIUS);
              const fontSize = labelSizes[index] || 24;
              return <g key={item.id} data-selected={index === selectedIndex ? 'true' : 'false'}>
                {items.length === 1 ? <circle cx="200" cy="200" r="185" fill={colors[index]} stroke={index === selectedIndex ? '#ffc84f' : 'white'} strokeWidth="4" /> : <path d={segmentPath(index, items.length)} fill={colors[index]} stroke={index === selectedIndex ? '#ffc84f' : 'white'} strokeWidth={index === selectedIndex ? 6 : 4} />}
                {index === selectedIndex && (items.length === 1 ? <circle cx="200" cy="200" r="185" fill="#ffffff" opacity=".2" /> : <path d={segmentPath(index, items.length)} fill="#ffffff" opacity=".2" />)}
                <text ref={(element) => { labelElements.current[index] = element; }} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize={fontSize} fontWeight="500" fill="#55318a"
                  transform={`rotate(${labelRotation(index, items.length)} ${x} ${y})`}>{item.text.toLocaleLowerCase('tr-TR')}</text>
              </g>;
            })}
            </g>
          </svg>
          <Box className="word-wheel-hub" aria-hidden="true"><PandaFace /></Box>
        </Box>
      </Box>}
    <Stack direction="row" justifyContent="center" className="word-wheel-actions">
      <Button variant="outlined" className="word-wheel-spin-button" startIcon={<ReplayRoundedIcon />} onClick={spin} disabled={(!items.length && !pendingEntries?.length) || spinning || paused}>{spinning ? 'Çark dönüyor…' : result ? 'Tekrar Çevir' : items.length === 1 ? 'İçeriği Göster' : 'Çarkı Çevir'}</Button>
    </Stack>
  </Box>;
}

export default function WheelModal({ fullScreen = false, loadContent, content, pendingContent, paused = false, onSettings, onClose, soundEnabled }) {
  const [data, setData] = useState(() => content || null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    if (content) { setData(content); return () => { active = false; }; }
    setData(null); setError('');
    loadContent().then((payload) => { if (active) setData(payload); }).catch((err) => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [loadContent, content]);
  return <Dialog open fullScreen={fullScreen} transitionDuration={fullScreen ? 0 : undefined} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="word-wheel-title" className="word-wheel-dialog" BackdropProps={{ className: 'word-wheel-backdrop' }} PaperProps={{ className: `word-wheel-modal${data?.qa_preview ? ' word-wheel-modal--qa' : ''}` }}>
    <Box className="word-wheel-sparkles" aria-hidden="true"><span>✦</span><span>✧</span><span>✦</span><span>✧</span></Box>
    <DialogTitle id="word-wheel-title" className="word-wheel-title">Hece & Kelime Çarkı
      {onSettings && <Button onClick={onSettings} className="word-wheel-settings">Ayarlar</Button>}
      <IconButton aria-label="Çarkı kapat" onClick={onClose} className="word-wheel-close"><CloseRoundedIcon /></IconButton>
      <Typography variant="body2">Hadi çarkı çevir, birlikte okuyalım!{data?.class?.code ? ` · ${data.class.code}` : ''}</Typography>
    </DialogTitle>
    <DialogContent>
      {error && <Alert severity="error">{error}</Alert>}
      {!error && !data && <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress aria-label="Çark yükleniyor" /></Box>}
      {data?.qa_preview && <Alert severity={data.entries.some((entry) => entry.reviewStatus !== 'approved') ? 'warning' : 'info'} sx={{ mb: 2 }}>İçerik QA önizlemesi · {data.entries.some((entry) => entry.reviewStatus !== 'approved') ? 'Bu adaylar pedagojik onay bekliyor.' : 'Onaylı içerikler.'}</Alert>}
      {data && <WheelPlayer entries={data.entries} pendingEntries={pendingContent?.entries} paused={paused} soundEnabled={soundEnabled} />}
    </DialogContent>
  </Dialog>;
}
